/* eslint-disable no-param-reassign */
import store, { AppDispatch } from 'app/store';
import { captureCdSnapshot } from 'components/watchers/concealed-duct-revert-watcher';
import { PTAC_UNIT_GRID_SQUARES, ptacUnitRoomId } from '@alpinehomeair/common';
import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  AirHandlerTypeCodeType,
  ConfigurationType,
  GridSpaceDataType,
  LevelType
} from 'data/types';
import { setConfigurations } from 'features/configurations';
import { updateStructureItemAttribute } from 'features/structure';
import { current, produce } from 'immer';
import { getDefaultReturnEndpointPxForZone } from 'utils/concealed-duct-geometry';
import {
  AirHandlerMoveTargetArgsType,
  MoveAirHandlerArgsType,
  ZoneOwnerType
} from 'utils/equipment-move-types';
import {
  findZoneContainingSpace,
  markCondensersStale
} from 'utils/configurations';
import {
  isUserPositionedOnPlacement,
  snapAirHandlerIntoSpace,
  zoneGridSpaces
} from 'utils/air-handler-placement';
import {
  findLevelForSpace,
  findNearestExternalEdge,
  findNearestSpace,
  getContainingSpace,
  getGridSpacesForLevel,
  isBottomLevel,
  isInsideAnySpace,
  isTopLevel,
  moveOutsideSpaces
} from 'utils/grid';
import {
  syncAirHandlerLinesetAnchors,
  syncCondenserLinesetAnchors
} from 'utils/lineset-anchors';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import { clampPointToStructureFootprint } from 'utils/point-clamp';
import { isPtacPreference } from 'utils/project';
import { ptacLevelContextFor } from 'utils/ptac-level-context';
import { snapPtacUnitToRoom } from 'utils/ptac-unit-placement';
import { getAirHandlerTypeLabelFromCode } from 'utils/structure';
import { structureOrientationSetting } from 'utils/structure-orientation';
import { isAirHandlerTypeAllowedInZone } from 'utils/zone-restrictions';

const GRID_SQUARES = 3;

type MoveCondenserArgs = {
  dispatch: AppDispatch;
  configurations: ConfigurationType[];
  configurationUniqueId: string;
  condenserUniqueId: string;
  x: number;
  y: number;
  levels: LevelType[];
  levelIndex: number;
  gridSpaces: GridSpaceDataType[];
};

export function moveCondenserToGridPosition({
  dispatch,
  configurations,
  configurationUniqueId,
  condenserUniqueId,
  x: targetX,
  y: targetY,
  levels,
  levelIndex,
  gridSpaces
}: MoveCondenserArgs): void {
  const objectSize = GRID_SQUARES * STRUCTURE_GRID_SIZE;

  let x = targetX;
  let y = targetY;

  const activeConfig = configurations.find(
    (c) => c.uniqueId === configurationUniqueId
  );
  if (!activeConfig) return;

  const condenser = activeConfig.condensers.find(
    (c) => c.uniqueId === condenserUniqueId
  );
  if (!condenser) return;

  const { placementType } = condenser;
  const ptacRoom = isPtacPreference()
    ? gridSpaces.find((space) => space.spaceId === ptacUnitRoomId(condenser))
    : undefined;

  if (ptacRoom) {
    const snapped = snapPtacUnitToRoom(
      ptacRoom,
      ptacLevelContextFor(
        levels,
        levels[levelIndex],
        gridSpaces,
        structureOrientationSetting(store.getState().project.projectSettings)
      ),
      { x, y }
    );
    x = snapped.x;
    y = snapped.y;
  } else if (placementType === 'ground') {
    if (!isBottomLevel(levels, levelIndex)) {
      return;
    }
    if (isInsideAnySpace({ x, y }, objectSize, gridSpaces)) {
      const newPosition = moveOutsideSpaces({ x, y }, objectSize, gridSpaces);
      x = newPosition.x;
      y = newPosition.y;
    }
  } else if (placementType === 'wall') {
    const externalEdge = findNearestExternalEdge(
      { x, y },
      objectSize,
      gridSpaces,
      getEffectiveFootprintPolygons(levels[levelIndex])
    );
    if (externalEdge) {
      x = externalEdge.x;
      y = externalEdge.y;
    }
  } else if (placementType === 'roof') {
    if (!isTopLevel(levels, levelIndex)) {
      return;
    }
    if (!isInsideAnySpace({ x, y }, objectSize, gridSpaces)) {
      const nearestSpace = findNearestSpace({ x, y }, objectSize, gridSpaces);
      if (nearestSpace) {
        x = nearestSpace.position.x / STRUCTURE_GRID_SIZE;
        y = nearestSpace.position.y / STRUCTURE_GRID_SIZE;
      }
    }
  }

  const gridSquaresCenterOffset =
    (ptacRoom ? PTAC_UNIT_GRID_SQUARES : GRID_SQUARES) / 2;
  const finalX = x;
  const finalY = y;
  const centerX = finalX + gridSquaresCenterOffset;
  const centerY = finalY + gridSquaresCenterOffset;

  const updatedConfigurations = produce(configurations, (draft) => {
    const config = draft.find((c) => c.uniqueId === activeConfig.uniqueId);
    if (config) {
      config.modified = true;
      const draftCondenser = config.condensers.find(
        (c) => c.uniqueId === condenserUniqueId
      );
      if (draftCondenser) {
        draftCondenser.position = {
          x: finalX,
          y: finalY
        };
        draftCondenser.modified = true;
      }

      syncCondenserLinesetAnchors(
        config,
        condenserUniqueId,
        { x: centerX, y: centerY },
        condenser.levelUniqueId
      );
    }
  });
  const finalConfigurations = updatedConfigurations.map((cfg) =>
    cfg.uniqueId === activeConfig.uniqueId
      ? markCondensersStale(cfg, [condenserUniqueId])
      : cfg
  );
  dispatch(setConfigurations(finalConfigurations));
}

function findZoneForZoneItem(
  config: ConfigurationType,
  zoneItemId: string
): ZoneOwnerType | null {
  for (const condenser of config.condensers) {
    const zone = condenser.zones.find((candidate) =>
      candidate.zoneItems.some((zi) => zi.uniqueId === zoneItemId)
    );
    if (zone) {
      return { condenser, zone };
    }
  }
  return null;
}

function isConcealedAirHandlerType(
  airHandlerTypeCode: AirHandlerTypeCodeType | null
): boolean {
  return (
    airHandlerTypeCode === 'concealed' ||
    airHandlerTypeCode === 'concealed_high_static'
  );
}

function resolveAirHandlerTargetGridSpace(
  point: { x: number; y: number },
  gridSpaces: GridSpaceDataType[]
): GridSpaceDataType | null {
  const objectSize = GRID_SQUARES * STRUCTURE_GRID_SIZE;
  return (
    getContainingSpace(point, objectSize, gridSpaces) ??
    findNearestSpace(point, objectSize, gridSpaces)
  );
}

function zoneContainsSpace(
  zone: ZoneOwnerType['zone'],
  spaceId: string
): boolean {
  return zone.zoneItems.some((zi) => zi.structureItemId === spaceId);
}

export function airHandlerMoveChangesZone({
  configurations,
  configurationUniqueId,
  zoneItemId,
  x,
  y,
  gridSpaces
}: AirHandlerMoveTargetArgsType): boolean {
  const activeConfig = configurations.find(
    (c) => c.uniqueId === configurationUniqueId
  );
  const owner = activeConfig
    ? findZoneForZoneItem(activeConfig, zoneItemId)
    : null;
  if (!owner) return false;
  if (
    isConcealedAirHandlerType(owner.zone.airHandler?.airHandlerTypeCode ?? null)
  ) {
    return false;
  }
  const targetGridSpace = resolveAirHandlerTargetGridSpace(
    { x, y },
    gridSpaces
  );
  return (
    targetGridSpace !== null &&
    !zoneContainsSpace(owner.zone, targetGridSpace.spaceId)
  );
}

export function moveAirHandlerToGridPosition({
  dispatch,
  configurations,
  configurationUniqueId,
  zoneItemId,
  x: targetX,
  y: targetY,
  gridSpaces
}: MoveAirHandlerArgsType): void {
  const { structure } = store.getState().structure;
  const objectSize = GRID_SQUARES * STRUCTURE_GRID_SIZE;

  let x = targetX;
  let y = targetY;

  const activeConfig = configurations.find(
    (c) => c.uniqueId === configurationUniqueId
  );
  if (!activeConfig) return;

  const owner = findZoneForZoneItem(activeConfig, zoneItemId);
  const airHandlerTypeCode: AirHandlerTypeCodeType | null =
    owner?.zone.airHandler?.airHandlerTypeCode ?? null;

  if (isConcealedAirHandlerType(airHandlerTypeCode)) {
    if (!owner) return;
    const owningZone = owner.zone;
    const owningZoneId = owningZone.uniqueId;
    const condenserId = owner.condenser.uniqueId;
    const owningAirHandlerUniqueId = owningZone.airHandler?.uniqueId;
    const owningZoneLevelUniqueId = owningZone.zoneItems[0]
      ? findLevelForSpace(owningZone.zoneItems[0].structureItemId, structure)
          ?.uniqueId
      : undefined;

    const halfSquares = GRID_SQUARES / 2;
    const proposedCenterPx = {
      x: (x + halfSquares) * STRUCTURE_GRID_SIZE,
      y: (y + halfSquares) * STRUCTURE_GRID_SIZE
    };

    const clampedCenterPx = clampPointToStructureFootprint(
      proposedCenterPx,
      gridSpaces
    );
    const newPositionX = clampedCenterPx.x / STRUCTURE_GRID_SIZE - halfSquares;
    const newPositionY = clampedCenterPx.y / STRUCTURE_GRID_SIZE - halfSquares;

    const frozenReturnEndpointPx = owningZone.returnOverride
      ? null
      : getDefaultReturnEndpointPxForZone(owningZone, structure, (levelIndex) =>
          getGridSpacesForLevel(structure, levelIndex)
        );

    captureCdSnapshot(activeConfig, {
      source: 'air-handler-move',
      zoneUniqueId: owningZoneId
    });

    const updatedConfigurations = produce(configurations, (draft) => {
      const config = draft.find((c) => c.uniqueId === activeConfig.uniqueId);
      if (!config) return;
      config.condensers.forEach((condenser) => {
        condenser.zones.forEach((zone) => {
          if (zone.uniqueId !== owningZoneId) return;
          if (!zone.airHandler) return;
          zone.airHandler.positionX = newPositionX;
          zone.airHandler.positionY = newPositionY;
          zone.airHandler.userPositioned = true;
          zone.airHandler.modified = true;
          if (frozenReturnEndpointPx && !zone.returnOverride) {
            zone.returnOverride = frozenReturnEndpointPx;
          }
          zone.modified = true;
          config.modified = true;
        });
      });
      if (owningAirHandlerUniqueId) {
        syncAirHandlerLinesetAnchors(
          config,
          owningAirHandlerUniqueId,
          {
            x: clampedCenterPx.x / STRUCTURE_GRID_SIZE,
            y: clampedCenterPx.y / STRUCTURE_GRID_SIZE
          },
          owningZoneLevelUniqueId
        );
      }
    }).map((cfg) =>
      cfg.uniqueId === activeConfig.uniqueId
        ? markCondensersStale(cfg, [condenserId])
        : cfg
    );

    dispatch(setConfigurations(updatedConfigurations));
    return;
  }

  const currentZone = owner?.zone ?? null;
  const targetGridSpace = resolveAirHandlerTargetGridSpace(
    { x, y },
    gridSpaces
  );

  let destinationZoneResult: ReturnType<typeof findZoneContainingSpace> = null;
  let isMovingToNewZone = false;

  if (targetGridSpace && currentZone) {
    if (!zoneContainsSpace(currentZone, targetGridSpace.spaceId)) {
      isMovingToNewZone = true;
      destinationZoneResult = findZoneContainingSpace(
        activeConfig,
        targetGridSpace.spaceId
      );
      if (!destinationZoneResult || destinationZoneResult.zone.airHandler) {
        return;
      }
      if (
        airHandlerTypeCode &&
        !isAirHandlerTypeAllowedInZone(
          airHandlerTypeCode,
          destinationZoneResult.condenser,
          destinationZoneResult.zone.uniqueId
        )
      ) {
        return;
      }
    }
  }

  if (targetGridSpace && airHandlerTypeCode) {
    const snapZone = isMovingToNewZone
      ? destinationZoneResult?.zone
      : currentZone;
    ({ x, y } = snapAirHandlerIntoSpace({
      airHandlerTypeCode,
      position: { x, y },
      objectSize,
      space: targetGridSpace,
      wallSnapOptions: { allGridSpaces: gridSpaces, structure },
      zoneSpaces: zoneGridSpaces(snapZone, gridSpaces)
    }));
  }

  const gridSquaresCenterOffset = GRID_SQUARES / 2;
  const centerX = x + gridSquaresCenterOffset;
  const centerY = y + gridSquaresCenterOffset;
  const userPositioned =
    airHandlerTypeCode !== null &&
    isUserPositionedOnPlacement(airHandlerTypeCode);

  const targetZone = isMovingToNewZone
    ? destinationZoneResult?.zone
    : currentZone;
  const targetZoneLevelUniqueId = targetZone?.zoneItems[0]
    ? findLevelForSpace(targetZone.zoneItems[0].structureItemId, structure)
        ?.uniqueId
    : undefined;

  const updatedConfigurations = produce(configurations, (draft) => {
    const config = draft.find((c) => c.uniqueId === activeConfig.uniqueId);

    if (config) {
      let actualAirHandlerUniqueId: string | undefined;

      if (isMovingToNewZone && destinationZoneResult) {
        let sourceZone: (typeof config.condensers)[0]['zones'][0] | undefined;
        let destZone: (typeof config.condensers)[0]['zones'][0] | undefined;

        for (const condenser of config.condensers) {
          for (const zone of condenser.zones) {
            if (zone.zoneItems.some((zi) => zi.uniqueId === zoneItemId)) {
              sourceZone = zone;
            }
            if (zone.uniqueId === destinationZoneResult.zone.uniqueId) {
              destZone = zone;
            }
          }
        }

        if (sourceZone?.airHandler && destZone) {
          actualAirHandlerUniqueId = sourceZone.airHandler.uniqueId;
          destZone.airHandler = {
            ...current(sourceZone.airHandler),
            positionX: x,
            positionY: y,
            ...(userPositioned ? { userPositioned: true } : {}),
            modified: true
          };
          delete sourceZone.airHandler;
          sourceZone.modified = true;
          destZone.modified = true;
          config.modified = true;
        }
      } else {
        config.condensers.forEach((condenser) => {
          condenser.zones.forEach((zone) => {
            const zoneItem = zone.zoneItems.find(
              (zi) => zi.uniqueId === zoneItemId
            );
            if (zoneItem && zone.airHandler) {
              actualAirHandlerUniqueId = zone.airHandler.uniqueId;
              zone.airHandler.positionX = x;
              zone.airHandler.positionY = y;
              if (userPositioned) {
                zone.airHandler.userPositioned = true;
              }
              zone.modified = true;
              config.modified = true;
            }
          });
        });
      }

      if (actualAirHandlerUniqueId) {
        syncAirHandlerLinesetAnchors(
          config,
          actualAirHandlerUniqueId,
          { x: centerX, y: centerY },
          targetZoneLevelUniqueId
        );
      }
    }
  });

  dispatch(setConfigurations(updatedConfigurations));

  if (isMovingToNewZone && destinationZoneResult && airHandlerTypeCode) {
    const airHandlerLabel = getAirHandlerTypeLabelFromCode(airHandlerTypeCode);
    if (airHandlerLabel) {
      destinationZoneResult.zone.zoneItems.forEach((zoneItem) => {
        dispatch(
          updateStructureItemAttribute({
            uniqueId: zoneItem.structureItemId,
            attributeCode: 'preferred_air_handler_type',
            value: airHandlerLabel
          })
        );
      });
    }
  }
}
